/**
 * 小窝里 TA 此刻站在哪、在不在走（画面用；事实位置只读后端，见 homeStage）：
 * - 有转场：按真实起止时刻从起点走到终点（跨房间时走到门口离开，或从门口走进来）；
 * - 醒着、没有具体在做的事、并且有身体图：在自己那一点附近走走停停，每走三步回一次原点（站姿图的程序动作，只算布局样片，不是走路动作帧——c84a D2116-5）；
 * - 睡着、有具体在做的事、刚到家、说不清：站（趴）在那一点上不走；
 * - 刚从睡着变成醒着：先伸个懒腰（waking）再动。
 * 减少动态效果：不走、不转场动画、不伸懒腰——直接站在事实位置上，状态靠姿态和文字看懂。
 * 换宠物、换房间、事实变了、卸载：所有定时器当场清掉，不留一个晚到的回调把 TA 挪走。
 * 路线先绕开挡路的地方（routeAround：茶几桌腿那一圈），走过遮挡片落地那一行时再切成几小段（splitAtBaselines）：
 * at 是这一小段的终点，遮挡片按它在上还是在下，跨线那一刻才换。
 * UI028: HomeScene supplies pathGate. The legacy ungated branch remains for
 * standalone callers; the actual scene plans a vector and awaits loaded gait.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { HomeLife, HomeRoom } from "@/shared/contracts";
import { serverNow } from "@/shared/time/clock";
import { anchorPoint, pathLength, pauseMs, pickWander, routeAround, seededRandom, shownAnchor, splitAtBaselines, stageMode, transitIn, walkMsFor, wanderBox, type StageMode, type StagePoint } from "./homeStage";
import { stageFacing } from "./homeSceneMotion";
import type { HomeFacing } from "./homeMotionPolicy";

export type PetStage = {
  at: StagePoint;
  /** Current interpolated foot: depth is never switched to the destination early. */
  depthAt: StagePoint;
  /** 走到 at 要多久（0＝瞬移，比如刚挂上、换了事实） */
  moveMs: number;
  walking: boolean;
  /** 转场走出了这一间（到门口了）：不再画 */
  gone: boolean;
  waking: boolean;
  mode: StageMode;
  /** Planned vector can prepare gait before any position starts moving. */
  facing?: HomeFacing | null;
  motionRequested?: boolean;
};

/** 每走几步回一次原点（第 RETURN_EVERY 步回去） */
export const RETURN_EVERY = 4;
/** 伸懒腰多久（与 home.css ps-pet-stretch 一致） */
export const WAKE_MS = 1400;
/** 还没开始的转场最多提前多久等它（更远的交给页面原有的定时重取） */
export const PENDING_LIMIT_MS = 10 * 60_000;

export interface ScenePathGate { key: string; enabled: boolean; disabled: boolean; allowed: boolean; facing: HomeFacing | null }
export function usePetStage({ petId, room, life, reduced, wander = true, pathGate }: { petId: string; room: HomeRoom; life: HomeLife | null; reduced: boolean; /** Legacy callers only; HomeScene always supplies the explicit path gate. */ wander?: boolean; pathGate?: ScenePathGate }): PetStage {
  const gated = useScenePetStage({ petId, room, life, reduced, wander, pathGate });
  const mode = stageMode(life);
  // 画在哪个锚点（看窗外时画在窗边那一点，见 homeStage.shownAnchor）
  const anchor = shownAnchor(room, life);
  const home = anchorPoint(room, anchor);
  const transition = life?.position?.transition ?? null;
  const transitKey = transition ? `${transition.from_room}:${transition.from_anchor}>${transition.to_room}:${transition.to_anchor}@${transition.starts_at}/${transition.ends_at}` : "";
  const [stage, setStage] = useState<Omit<PetStage, "mode" | "waking" | "depthAt">>({ at: home, moveMs: 0, walking: false, gone: false });
  const [depthAt, setDepthAt] = useState(home);
  const [waking, setWaking] = useState(false);
  // 转场还没开始时，到开始那一刻拨一下，让下面的效果重算（这时 transitIn 才给出这一段）
  const [nudge, setNudge] = useState(0);
  // 上一刻是哪只、什么状态：换了宠物不算“醒来”
  const prevMode = useRef(`${petId}:${mode}`);

  useEffect(() => {
    if (pathGate) return undefined;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const later = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
    const now = serverNow();
    setDepthAt(home);
    const canTravel = mode !== "sleeping" && mode !== "away" && mode !== "unknown";
    const startsIn = transition && !reduced && canTravel ? Date.parse(transition.starts_at) - now : NaN;
    if (transition && startsIn > 0 && startsIn <= PENDING_LIMIT_MS) {
      // 后端已经定了这一段、还没到出发时刻：先待在起点（起点不在这一间就照事实位置），到点再走
      setStage({ at: transition.from_room === room ? anchorPoint(room, transition.from_anchor) : home, moveMs: 0, walking: false, gone: false });
      setDepthAt(transition.from_room === room ? anchorPoint(room, transition.from_anchor) : home);
      later(startsIn, () => setNudge((n) => n + 1));
      return () => timers.forEach(clearTimeout);
    }
    // 从 from 走到 to：先绕开挡路的地方（茶几桌腿），每一段再在遮挡片落地那一行切开；
    // 每一小段到了再给下一段（遮挡片在 TA 上面还是下面按每一小段的终点算），各段时长按路长分
    const walk = (from: StagePoint, to: StagePoint, totalMs: number, done: () => void) => {
      const route = routeAround(room, from, to);
      const points = route.flatMap((p, i) => splitAtBaselines(room, i ? route[i - 1] : from, p));
      const length = Math.max(1, pathLength(from, points));
      let at = from;
      const leg = (i: number) => {
        const next = points[i];
        const ms = Math.round((Math.hypot(next.x - at.x, next.y - at.y) / length) * totalMs);
        const from = at;
        at = next;
        setDepthAt(from);
        const started = Date.now();
        const depthTick = () => {
          const share = Math.min(1, (Date.now() - started) / Math.max(1, ms));
          setDepthAt({ x: from.x + (next.x - from.x) * share, y: from.y + (next.y - from.y) * share });
          if (share < 1) later(Math.min(33, ms), depthTick);
        };
        later(Math.min(33, ms), depthTick);
        setStage({ at: next, moveMs: ms, walking: true, gone: false });
        later(ms, () => (i + 1 < points.length ? leg(i + 1) : done()));
      };
      leg(0);
    };
    const transit = reduced || !canTravel ? null : transitIn(transition, room, now);
    if (transit) {
      setDepthAt(transit.from);
      setStage({ at: transit.from, moveMs: 0, walking: false, gone: false });
      // 下一拍再给终点：先在起点画出来，过渡才有起点
      later(40, () => walk(transit.from, transit.to, transit.remainingMs, () => setStage({ at: transit.to, moveMs: 0, walking: false, gone: transit.leaves })));
      return () => timers.forEach(clearTimeout);
    }
    setStage({ at: home, moveMs: 0, walking: false, gone: false });
    if (mode !== "free" || reduced || !wander) return () => timers.forEach(clearTimeout);
    const random = seededRandom(`${petId}:${room}:${anchor ?? "-"}`);
    const box = wanderBox(room, home);
    let here = home;
    const step = (n: number) => {
      later(pauseMs(random), () => {
        const next = n % RETURN_EVERY === RETURN_EVERY - 1 ? home : pickWander(random, box, here);
        const from = here;
        here = next;
        walk(from, next, walkMsFor(pathLength(from, routeAround(room, from, next))), () => {
          setStage({ at: next, moveMs: 0, walking: false, gone: false });
          step(n + 1);
        });
      });
    };
    step(0);
    return () => timers.forEach(clearTimeout);
    // home 由 room、anchor 决定，不单独列
  }, [petId, room, anchor, mode, reduced, wander, transitKey, nudge, !!pathGate]);

  // 刚醒：伸个懒腰（只在这一页亲眼看到睡着→醒着时）
  useEffect(() => {
    if (pathGate) return undefined;
    const was = prevMode.current;
    prevMode.current = `${petId}:${mode}`;
    if (reduced || was !== `${petId}:sleeping` || (mode !== "free" && mode !== "busy")) return undefined;
    setWaking(true);
    const timer = setTimeout(() => setWaking(false), WAKE_MS);
    return () => {
      clearTimeout(timer);
      setWaking(false);
    };
  }, [petId, mode, reduced, !!pathGate]);

  return pathGate ? gated : { ...stage, depthAt, waking, mode };
}

/**
 * 开走前先在起点画一拍（毫秒），框才有起点可过渡（和不带步态那条分支的 later(40) 一样）。这一拍算进转场的展示：
 * 各段时长照挂上时算好的剩余时间分，展示的截止时刻跟着顺延这一拍（UI2-TESTDEBT-02）。
 */
export const START_FRAME_MS = 40;

/** Qualified scene path. Facts and decorative paths have separate cancellation. */
function useScenePetStage({ petId, room, life, reduced, wander, pathGate }: { petId: string; room: HomeRoom; life: HomeLife | null; reduced: boolean; wander: boolean; pathGate?: ScenePathGate }): PetStage {
  const mode = stageMode(life), anchor = shownAnchor(room, life), home = anchorPoint(room, anchor);
  const transition = life?.position?.transition ?? null;
  const key = JSON.stringify([petId, room, anchor, mode, transition, reduced, pathGate?.key, pathGate?.enabled, pathGate?.disabled, wander]);
  const allowedMode = mode !== "sleeping" && mode !== "away" && mode !== "unknown";
  /**
   * 此刻的事实位置：不在走的时候框就画在这里。
   * - 还没到出发时刻：待在起点（I 10-13 定口径，UI2-TESTDEBT-02）。行程还没发生，最新事实是它还在原地，不提前画到终点
   *   （起点不在这一间就照事实位置，同旧分支）。只管 PENDING_LIMIT_MS（10 分钟）以内的：更远的不排开走计时，和旧分支一样照事实锚点画，
   *   免得停在起点一直等到 end、或闲逛从起点一下跳到锚点（6c2b 审图）；
   * - 其余照 UI028（Q069 已逐条接受）：已经在路上而步态还没加载合格时画在事实锚点，步态晚到时由 walk 按后端此刻的脚底重新投影开走；
   *   走完了、没有转场、睡着／出门／说不清也都是事实锚点。
   */
  const factAt = (t: number): StagePoint => {
    if (!transition || !allowedMode) return home;
    const begins = Date.parse(transition.starts_at);
    return Number.isFinite(begins) && t < begins && begins - t <= PENDING_LIMIT_MS && transition.from_room === room ? anchorPoint(room, transition.from_anchor) : home;
  };
  const still = (t: number = serverNow()): PetStage => { const at = factAt(t); return { at, depthAt: at, moveMs: 0, walking: false, gone: false, waking: false, mode, facing: null, motionRequested: false }; };
  const [shown, setShown] = useState<{ key: string; stage: PetStage }>(() => ({ key, stage: still() }));
  const current = useRef(pathGate); current.current = pathGate;
  const engine = useRef<{ key: string; pump: () => void } | null>(null);
  const [waking, setWaking] = useState(false);
  const previousMode = useRef(`${petId}:${mode}`);
  useLayoutEffect(() => {
    const was = previousMode.current; previousMode.current = `${petId}:${mode}`;
    const wake = !!pathGate && !reduced && !pathGate.disabled && was === `${petId}:sleeping` && (mode === "free" || mode === "busy");
    setWaking(wake);
    if (!wake) return undefined;
    const timer = setTimeout(() => setWaking(false), WAKE_MS);
    return () => { clearTimeout(timer); };
  }, [petId, mode, reduced, pathGate?.disabled, !!pathGate]);
  useLayoutEffect(() => {
    if (!pathGate) return undefined;
    let alive = true, running = false;
    let pending: (() => void) | null = null;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const factTimers = new Set<ReturnType<typeof setTimeout>>();
    const later = (ms: number, fn: () => void) => {
      const timer = setTimeout(() => { timers.delete(timer); if (alive) fn(); }, Math.max(0, ms)); timers.add(timer);
    };
    const clear = () => { timers.forEach(clearTimeout); timers.clear(); };
    const dispose = () => { alive = false; clear(); factTimers.forEach(clearTimeout); factTimers.clear(); engine.current = null; };
    const paint = (stage: PetStage) => { if (alive) setShown({ key, stage }); };
    let visual = still();
    const update = (extra: Partial<PetStage>) => { visual = { ...visual, ...extra }; paint(visual); };
    paint(visual);
    const permitted = () => !!current.current?.allowed && current.current.key === pathGate.key && current.current.facing === visual.facing && !current.current.disabled;
    const pump = () => {
      if (!alive) return;
      if (running && !permitted()) {
        // Revocation cancels every interpolation timer now. Keep latest facts,
        // never a stale midpoint or another identity's destination.
        clear(); running = false; pending = null; visual = still(); paint(visual);
      } else if (pending && permitted()) { const start = pending; pending = null; start(); }
    };
    engine.current = { key, pump };
    const now = serverNow();
    const end = transition ? Date.parse(transition.ends_at) : null;
    // 展示的截止时刻：起点那一拍画过以后顺延 START_FRAME_MS（见 armFact）；事实本身仍是 end
    const transit = transitIn(transition, room, now);
    const random = seededRandom(`${petId}:${room}:${anchor ?? "-"}`);
    const box = wanderBox(room, home);
    const finishFact = () => {
      clear(); pending = null; running = false;
      update({ at: home, depthAt: home, moveMs: 0, walking: false, motionRequested: false,
        gone: transition?.from_room === room && transition.to_room !== room });
    };
    // Server fact boundaries survive absent/revoked gait and disabled animation.
    // Only movement timers are cancelled by a capability loss.
    let deadline = end;
    const armFact = (at: number) => {
      factTimers.forEach(clearTimeout); factTimers.clear();
      const timer = setTimeout(() => { factTimers.delete(timer); if (alive) finishFact(); }, Math.max(0, at - serverNow()));
      factTimers.add(timer);
    };
    if (allowedMode && end !== null && Number.isFinite(end)) {
      if (end <= now) finishFact();
      else armFact(end);
    }
    // 还没出发、画在起点（factAt）：到了出发时刻事实变了。这一刻要开走的（步态可用、没关动画）由下面的 pending 分支从起点画过去；
    // 不走的照 UI028 换到事实锚点，不一直停在起点（UI2-TESTDEBT-02）
    const begins = transition ? Date.parse(transition.starts_at) : NaN;
    const willWalk = pathGate.enabled && !pathGate.disabled && !reduced;
    if (allowedMode && !willWalk && Number.isFinite(begins) && begins > now && begins - now <= PENDING_LIMIT_MS) {
      const timer = setTimeout(() => { factTimers.delete(timer); if (alive && !running) { visual = still(); paint(visual); } }, begins - now);
      factTimers.add(timer);
    }
    if (!pathGate.enabled || pathGate.disabled || reduced || !allowedMode) return dispose;
    const walk = (from: StagePoint, to: StagePoint, totalMs: number, done: () => void, paintStart = false, replanned = false) => {
      const projectedAt = serverNow();
      const route = routeAround(room, from, to);
      const points = route.flatMap((p, i) => splitAtBaselines(room, i ? route[i - 1] : from, p));
      const length = Math.max(1, pathLength(from, points));
      let at = from;
      const leg = (i: number) => {
        const next = points[i]; if (!next) { done(); return; }
        const source = at; at = next;
        const facing = stageFacing(source, next);
        if (!facing) { leg(i + 1); return; }
        update({ walking: false, moveMs: 0, facing, motionRequested: true });
        pending = () => {
          if (deadline !== null && serverNow() >= deadline) { finishFact(); return; }
          if (i === 0 && end !== null && serverNow() > projectedAt) {
            // Loading/turning takes presentation time, never world time. Replan
            // from the server's current projected foot instead of an old start.
            const latest = transitIn(transition, room, serverNow());
            if (latest) walk(latest.from, latest.to, latest.remainingMs, done, paintStart, true);
            else finishFact();
            return;
          }
          running = true;
          const go = () => {
            const ms = Math.max(1, Math.min(Math.round(Math.hypot(next.x-source.x,next.y-source.y)/length*totalMs), deadline === null ? Infinity : deadline-serverNow()));
            // 步态晚到、按后端此刻的脚底重新投影的那一趟：起点那一拍画过以后再取一次此刻的脚底（UI028：不重放旧的起点，这一拍也不让它旧 40 毫秒）
            const from0 = i === 0 && paintStart && replanned ? (transitIn(transition, room, serverNow())?.from ?? source) : source;
            update({ at: next, depthAt: from0, moveMs: ms, walking: true });
            const started = Date.now();
            const depth = () => {
              if (!permitted()) { pump(); return; }
              const share = Math.min(1, (Date.now()-started)/ms);
              update({ depthAt: { x: from0.x+(next.x-from0.x)*share, y: from0.y+(next.y-from0.y)*share } });
              if (share < 1) later(Math.min(33, ms), depth);
            };
            later(Math.min(33, ms), depth);
            later(ms, () => { running = false; update({ at: next, depthAt: next, walking: false, moveMs: 0 }); leg(i+1); });
          };
          if (i === 0 && paintStart) {
            // 转场开走：先在起点画一拍（框此刻就在这一段的起点），下一拍再给这一段的终点；截止时刻顺延这一拍
            if (end !== null && Number.isFinite(end)) { deadline = end + START_FRAME_MS; armFact(deadline); }
            update({ at: source, depthAt: source, moveMs: 0, walking: false });
            later(START_FRAME_MS, () => { if (!permitted()) { pump(); return; } go(); });
          } else go();
        };
        pump();
      };
      leg(0);
    };
    const step = (at: StagePoint, n: number) => later(pauseMs(random), () => {
      const next = n%RETURN_EVERY === RETURN_EVERY-1 ? home : pickWander(random,box,at);
      walk(at,next,walkMsFor(pathLength(at,routeAround(room,at,next))), () => {
        update({ at: next, depthAt: next, walking: false, moveMs: 0, motionRequested: false }); step(next,n+1);
      });
    });
    if (transit) {
      // Gait loading never extends a server deadline or blocks latest fact updates.
      walk(transit.from,transit.to,transit.remainingMs, () => {
        update({ at: transit.to, depthAt: transit.to, moveMs: 0, walking: false, motionRequested: false, gone: transit.leaves });
      }, true);
    } else if (transition && Date.parse(transition.starts_at)>now && Date.parse(transition.starts_at)-now<=PENDING_LIMIT_MS) {
      // 还没到出发时刻：框已经画在起点（still → factAt），到点再从起点开走
      later(Date.parse(transition.starts_at)-now, () => {
        const start = transitIn(transition,room,serverNow());
        if (start) { walk(start.from,start.to,start.remainingMs,finishFact,true); }
      });
    } else if (mode === "free" && wander) step(home,0);
    return dispose;
  }, [key]);
  useLayoutEffect(() => { if (engine.current?.key === key) engine.current.pump(); }, [key,pathGate?.allowed,pathGate?.facing]);
  // Key/resource/fact changes cannot expose the previous stage even for one render.
  return { ...(shown.key === key ? shown.stage : still()), waking: waking && !reduced && !pathGate?.disabled && (mode === "free" || mode === "busy") };
}
